// Manager Web Push Client Management

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getPermissionStatus(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

export async function isSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (error) {
    console.error('Service worker registration failed:', error);
    return null;
  }
}

export async function enableNotifications(regionScope?: string, branchScope?: string[]): Promise<{ ok: boolean; message?: string }> {
  if (!isPushSupported()) {
    return { ok: false, message: 'เบราว์เซอร์นี้ไม่รองรับ Web Push Notifications' };
  }

  // 1. Explicitly request permission upon user click
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, message: 'ผู้ใช้ไม่อนุญาตการแจ้งเตือน (Permission denied)' };
  }

  // 2. Register service worker
  const reg = await registerServiceWorker();
  if (!reg) {
    return { ok: false, message: 'ไม่สามารถลงทะเบียน Service Worker ได้' };
  }

  // 3. Get VAPID public key from backend
  const keyRes = await fetch('/api/push');
  if (!keyRes.ok) {
    const err = await keyRes.json().catch(() => ({}));
    return { ok: false, message: (err as { error?: string }).error || 'ไม่สามารถดึง VAPID Key ได้' };
  }
  const { publicKey } = await keyRes.json() as { publicKey: string };

  // 4. Always unsubscribe old key to ensure fresh subscription with current VAPID key
  try {
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe().catch(() => {});
    }

    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });

    const subJson = sub.toJSON();

    // 5. Send subscription to server
    const saveRes = await fetch('/api/push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: sub.endpoint,
        keys: subJson.keys,
        managerId: 'manager-device',
        regionScope: regionScope || null,
        branchScope: branchScope || null
      })
    });

    if (!saveRes.ok) {
      return { ok: false, message: 'ไม่สามารถบันทึก subscription บนเซิร์ฟเวอร์ได้' };
    }

    return { ok: true, message: 'เปิดการแจ้งเตือนสำเร็จแล้ว' };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : 'เกิดข้อผิดพลาดในการ subscribe' };
  }
}

export async function disableNotifications(): Promise<{ ok: boolean; message?: string }> {
  if (!isPushSupported()) return { ok: true };

  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await fetch('/api/push', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint })
        }).catch(() => {});
      }
    }
    return { ok: true, message: 'ปิดการแจ้งเตือนแล้ว' };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : 'เกิดข้อผิดพลาดในการยกเลิก' };
  }
}

export async function sendTestNotification(): Promise<{ ok: boolean; message?: string }> {
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    const sub = await reg?.pushManager.getSubscription();
    const body = sub?.endpoint ? { endpoint: sub.endpoint } : {};

    const res = await fetch('/api/push/test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json() as { ok?: boolean; error?: string; deliveredCount?: number };
    if (!res.ok || !data.ok || !data.deliveredCount) {
      return {
        ok: false,
        message: data.error || 'ส่งการแจ้งเตือนไม่สำเร็จ กรุณาปิดแล้วเปิดสวิตช์แจ้งเตือนใหม่อีกครั้ง'
      };
    }
    return { ok: true, message: `ส่งการแจ้งเตือนทดสอบสำเร็จ (${data.deliveredCount} อุปกรณ์)` };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้' };
  }
}
